"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { Navigate, useNavigate } from "react-router-dom";
import { useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import PATHS from "@/routes/paths";
import { verifySsoToken } from "@/features/auth/api/login";
import useAuthStore from "@/features/auth/store/useAuthStore";
import { API_ENDPOINTS } from "@/constants/api";
import errorHandler from "@/features/auth/utils/errorHandler";
import envs from "@/envs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Spinner } from "@/components/ui/spinner";
import { CardDescription } from "@/components/ui/card.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";

const VERIFICATION_CODE_LENGTH = 6;

const verificationCodeSchema = z.object({
  verificationCode: z.string().length(VERIFICATION_CODE_LENGTH, { message: "" }),
});

type VerificationCodeFormValues = z.infer<typeof verificationCodeSchema>;

const VerificationCodeForm = () => {
  const email = useAuthStore((state) => state.email);
  const { redirectPath } = useUserStore();

  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(["auth"]);
  const form = useForm<VerificationCodeFormValues>({
    resolver: zodResolver(verificationCodeSchema),
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;
  const redirect = useNavigate();

  if (!email) return <Navigate to={PATHS.SIGN_IN} />;

  const onSubmit: SubmitHandler<VerificationCodeFormValues> = async ({ verificationCode }) => {
    try {
      const res = await verifySsoToken({ email, token: verificationCode });

      console.log(res);

      const callbackUrl = new URL(envs.VITE_API_URL + API_ENDPOINTS.AUTH.FINAL_CALLBACK);

      callbackUrl.searchParams.set("token", res.data.data.login_token);
      callbackUrl.searchParams.set("email", email);
      callbackUrl.searchParams.set("redirect_url", `${envs.VITE_SITE_URL}${redirectPath}`);

      window.open(callbackUrl, "_self");
    } catch (error: any) {
      form.setValue("verificationCode", "");
      if (error.response.data.code === "invalid_token") {
        form.setError("verificationCode", { message: t("auth:error.invalidCode") });
      } else if (error.response.data.code === "token_expired") {
        form.setError("verificationCode", { message: t("auth:error.codeExpired") });
        redirect(0);
      } else {
        errorHandler(error);
      }
    }
  };

  const shakeAnimation = {
    x: [0, -10, 10, -10, 10, 0],
    transition: { duration: 0.4 },
  };

  return (
    <form
      ref={formRef}
      className="flex max-w-full flex-col gap-6 p-10 lg:px-16 xl:px-36"
      onSubmit={handleSubmit(onSubmit)}
    >
      <CardDescription className="text-muted-foreground m-auto line-clamp-2 block w-3/4 max-w-80 text-center">
        {t(
          "auth:SignIn.pasteOrTypeVerificationCode",
          "Please paste or type your verification code:",
        )}
      </CardDescription>
      <motion.div
        animate={{ opacity: 1, x: 0 }}
        className="flex w-full justify-center gap-5"
        initial={{ opacity: 0, x: -100 }}
      >
        <Controller
          control={control}
          name="verificationCode"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <div className="relative">
                <motion.div animate={error ? shakeAnimation : {}}>
                  <InputOTP
                    containerClassName="mx-auto"
                    disabled={isSubmitting}
                    maxLength={VERIFICATION_CODE_LENGTH}
                    value={value}
                    onChange={onChange}
                    onComplete={() => {
                      formRef.current?.requestSubmit();
                    }}
                  >
                    {Array.from({ length: VERIFICATION_CODE_LENGTH }, (_, index) => (
                      <InputOTPGroup key={index} className="grow">
                        <InputOTPSlot
                          className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 h-14 w-12 max-w-12 grow text-2xl transition-all duration-200`}
                          index={index}
                        />
                      </InputOTPGroup>
                    ))}
                  </InputOTP>
                </motion.div>
                {isSubmitting ? (
                  <div className="bg-muted/30 absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center rounded-xl">
                    <Spinner className="bg-primary" size="md" />
                  </div>
                ) : null}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      className="text-destructive mt-2 p-1 text-sm"
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      {error.message}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }}
        />
      </motion.div>
    </form>
  );
};

// @ts-ignore
export default VerificationCodeForm;
